import { supabase } from './supabase';
import { letterDateDuJour } from '../utils/letterDate';
import { currentLanguage, t } from '../i18n';
import type {
  CoverLetter,
  CoverLetterParams,
  CvChanges,
  CvScore,
  FeatureKey,
  GeneratedCv,
  ImageInput,
  InterviewFeedback,
  InterviewMessage,
  InterviewRole,
  LinkedinAudit,
  RewrittenSection,
  LetterAnalysis,
  LetterChanges,
  LetterImproveDiagnostic,
  OutputLanguage,
  Plan,
} from '../types';

/**
 * Levée quand une Edge Function refuse l'appel IA pour quota dépassé (HTTP 402).
 * Déclenche le Paywall côté UI (Phase 2).
 */
/**
 * `cap_reached` : plafond MENSUEL atteint, les droits reviennent le 1er du mois.
 * `not_included` : la fonctionnalité n'est pas au plan, attendre ne changera rien.
 * Défaut prudent à `cap_reached` : une fonction serveur pas encore redéployée ne renvoie pas de
 * raison, et il vaut mieux inviter à revenir que déclarer à tort un blocage définitif.
 */
export type QuotaRefusal = 'cap_reached' | 'not_included';

export class QuotaError extends Error {
  constructor(
    public readonly feature: FeatureKey,
    public readonly reason: QuotaRefusal = 'cap_reached',
    /**
     * Plan appliqué par le serveur. Seul lui permet de savoir s'il reste quelque chose à vendre :
     * la lecture locale du plan peut être plus ancienne que celle qui a servi à refuser.
     */
    public readonly plan?: Plan,
  ) {
    super(`Quota dépassé pour ${feature}.`);
    this.name = 'QuotaError';
  }
}

/**
 * Levée quand le modèle juge l'offre hors de portée du CV (HTTP 422 `profil_trop_eloigne`). Aucun
 * crédit n'a été décompté. `raison` est la phrase factuelle du serveur, à afficher sous le message ;
 * null si le modèle n'en a pas donné.
 */
export class ProfilTropEloigneError extends Error {
  constructor(public readonly raison: string | null) {
    super(t('errors.profilTropEloigne'));
    this.name = 'ProfilTropEloigneError';
  }
}

const UNREADABLE_MESSAGE = 'CV illisible, essaie un autre format (PDF ou Word).';
// Annonce ou fiche de poste importée : le mot « CV » serait faux.
const UNREADABLE_OFFER_MESSAGE = 'Document illisible, essaie un autre format (PDF ou Word).';

interface ErrorPayload {
  error?: string;
  feature?: FeatureKey;
  /**
   * Distingue le PLAFOND MENSUEL atteint (`cap_reached`, les droits reviennent le 1er) de la
   * fonctionnalité hors plan (`not_included`, attendre ne changera rien). Absent tant qu'une
   * fonction serveur n'est pas redéployée : le défaut de `QuotaError` s'applique alors.
   */
  reason?: QuotaRefusal;
  /** Plan RÉELLEMENT appliqué au refus : dit s'il reste quelque chose à proposer. */
  plan?: Plan;
  /** `profil_trop_eloigne` : l'exigence de l'offre que le CV ne montre pas. */
  raison?: string | null;
  // Présent dans le corps, DÉLIBÉRÉMENT IGNORÉ. Il est rédigé en français côté serveur et décrit la
  // mécanique interne (JSON tronqué, message Postgres, exception) : l'afficher exposait la
  // structure du service et ne disait rien d'actionnable à la personne. Le diagnostic reste
  // possible : les Edge Functions journalisent déjà ce même détail.
  detail?: string;
}

// Message affiché par CODE d'erreur. Traduit : il s'adresse à l'utilisateur, il suit donc la langue
// de l'interface — à la différence du contenu généré, qui suit celle du document.
const ERROR_KEY_BY_CODE: Record<string, string> = {
  ai_bad_output: 'errors.aiBadOutput',
  server_error: 'errors.server',
  invalid_request: 'errors.invalidRequest',
  unauthorized: 'errors.unauthorized',
  // « Optimiser mon CV » : fiche de plus de 20 pages ou 10 000 caractères (contrôle serveur).
  fiche_trop_longue: 'errors.ficheTropLongue',
};

// Les Edge Functions renvoient un statut non-2xx avec un corps { error: '...' }.
// supabase-js expose ce corps via error.context (Response) — on le lit pour mapper.
async function invoke<T>(
  fn: string,
  body: Record<string, unknown>,
  feature: FeatureKey,
  friendlyErrors: Record<string, string> = {},
): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T & ErrorPayload>(fn, { body });

  if (error) {
    const payload = await readErrorPayload(error);
    throw mapError(payload, feature, friendlyErrors) ?? error;
  }

  // Garde-fou : certains transports remontent le corps d'erreur dans `data`.
  const dataError = mapError(data as ErrorPayload | null, feature, friendlyErrors);
  if (dataError) throw dataError;

  return data as T;
}

function mapError(
  payload: ErrorPayload | null,
  feature: FeatureKey,
  friendlyErrors: Record<string, string>,
): Error | null {
  if (!payload?.error) return null;
  if (payload.error === 'quota_exceeded') return new QuotaError(payload.feature ?? feature, payload.reason, payload.plan);
  if (payload.error === 'profil_trop_eloigne') return new ProfilTropEloigneError(payload.raison || null);
  if (friendlyErrors[payload.error]) return new Error(friendlyErrors[payload.error]);
  const key = ERROR_KEY_BY_CODE[payload.error];
  return key ? new Error(t(key)) : null;
}

async function readErrorPayload(error: unknown): Promise<ErrorPayload | null> {
  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    try {
      return (await context.clone().json()) as ErrorPayload;
    } catch {
      return null;
    }
  }
  return null;
}

export interface LinkedinInput {
  images?: ImageInput[];
}

/**
 * `stage` DÉCOUPE L'AUDIT EN DEUX APPELS, et il est optionnel côté serveur comme ici.
 *
 * `'audit'` rend le score et les priorités — ce que la personne attend, ~10 s au lieu de 90.
 * `'kit'` rend le kit de réécriture, et NE CONSOMME AUCUN QUOTA : c'est la seconde moitié d'un audit
 * déjà payé à l'étape précédente.
 * Absent, la réponse est complète en un appel : c'est ce que font les versions déjà installées.
 */
export type LinkedinStage = 'audit' | 'kit';

export interface LinkedinKit {
  optimization: NonNullable<LinkedinAudit['optimization']>;
}

export function scoreLinkedin(
  input: LinkedinInput,
  outputLanguage: OutputLanguage = 'fr',
  stage?: LinkedinStage,
): Promise<LinkedinAudit> {
  return invoke<LinkedinAudit>('claude-linkedin', { ...input, outputLanguage, stage }, 'linkedin_score');
}

/** Étape 2 depuis des captures. Le quota a déjà été consommé par l'étape 1. */
export function fetchLinkedinKit(
  input: LinkedinInput,
  outputLanguage: OutputLanguage = 'fr',
): Promise<LinkedinKit> {
  return invoke<LinkedinKit>(
    'claude-linkedin',
    { ...input, outputLanguage, stage: 'kit' },
    'linkedin_score',
  );
}

// Audit direct depuis le compte LinkedIn connecté (token côté serveur, aucune capture). Le même
// quota linkedin_score s'applique. Les erreurs de connexion/API remontent des messages clairs.
export function auditLinkedinAccount(
  outputLanguage: OutputLanguage = 'fr',
  stage?: LinkedinStage,
): Promise<LinkedinAudit> {
  return invoke<LinkedinAudit>('claude-linkedin', { source: 'account', outputLanguage, stage }, 'linkedin_score', {
    linkedin_not_connected: 'Connecte ton compte LinkedIn avant de lancer l’analyse.',
    linkedin_expired: 'Session LinkedIn expirée. Reconnecte ton compte, puis réessaie.',
    linkedin_api: 'Analyse LinkedIn impossible pour le moment. Réessaie, ou utilise les captures.',
  });
}

/** Étape 2 depuis le compte connecté. Le quota a déjà été consommé par l'étape 1. */
export function fetchLinkedinAccountKit(outputLanguage: OutputLanguage = 'fr'): Promise<LinkedinKit> {
  return invoke<LinkedinKit>(
    'claude-linkedin',
    { source: 'account', outputLanguage, stage: 'kit' },
    'linkedin_score',
  );
}

// Fiche de poste optionnelle : 3 modes d'entrée (texte / fichier PDF·Word / captures).
export interface CvOfferInput {
  text?: string;
  pdf?: { data: string };
  docx?: { data: string };
  images?: ImageInput[];
}

export interface CvScoreInput {
  pdf?: { data: string };
  docx?: { data: string };
  text?: string;
  offer?: CvOfferInput;
  // Titre de poste visé (facultatif). Note aussi l'adéquation CV/poste sans fiche de poste complète.
  targetRole?: string;
  // Titre du CV noté (nom du fichier sans extension), conservé pour la carte Mon CV. Facultatif.
  cvTitle?: string;
}

export function scoreCv(input: CvScoreInput): Promise<CvScore> {
  return invoke<CvScore>('claude-cv', { mode: 'score', ...input }, 'cv_score', {
    unreadable_document: UNREADABLE_MESSAGE,
    ai_bad_output: UNREADABLE_MESSAGE,
  });
}

export function generateCv(
  answers: string[],
  outputLanguage: OutputLanguage = 'fr',
): Promise<GeneratedCv> {
  return invoke<GeneratedCv>('claude-cv', { mode: 'create', answers, outputLanguage }, 'cv_create');
}

/**
 * Import : transpose un CV existant vers le schéma pour l'enregistrer dans « Mes CV ».
 * Consomme 1 cv_import — compteur DISTINCT de cv_create, qui vaut 0 en Free.
 * `ats_score` et `advice` reviennent vides : ce mode ne note pas, l'appelant y injecte le score
 * réel issu de la notation qui vient d'avoir lieu.
 */
export function extractCv(input: CvScoreInput): Promise<GeneratedCv> {
  return invoke<GeneratedCv>('claude-cv', { mode: 'extract', ...input }, 'cv_import', {
    unreadable_document: UNREADABLE_MESSAGE,
    ai_bad_output: UNREADABLE_MESSAGE,
  });
}

// ───────────── Parcours « Modifier mon CV » (premium, cv_improve) ─────────────
export interface CvImproveInput {
  pdf?: { data: string };
  docx?: { data: string };
  text?: string;
}
export interface ImproveAnswer {
  question: string;
  answer: string;
}
export interface ImproveQuestions {
  questions: string[];
  cvText: string;
}

// Étape 1 : questions ciblées + CV normalisé. Éligibilité abonné (Free → QuotaError → Paywall), sans conso.
export function improveCvQuestions(input: CvImproveInput): Promise<ImproveQuestions> {
  return invoke<ImproveQuestions>('claude-cv', { mode: 'improve_questions', ...input }, 'cv_improve', {
    unreadable_document: UNREADABLE_MESSAGE,
    ai_bad_output: UNREADABLE_MESSAGE,
  });
}

// Étape 2 : CV optimisé à partir des réponses, adapté à la fiche de poste si elle est fournie.
// Consomme 1 cv_improve (le livrable), avec ou sans fiche.
export function improveCvGenerate(cvText: string, answers: ImproveAnswer[], offer?: CvOfferInput): Promise<GeneratedCv> {
  return invoke<GeneratedCv>('claude-cv', { mode: 'improve_generate', cvText, answers, ...(offer ? { offer } : {}) }, 'cv_improve', {
    unreadable_document: UNREADABLE_MESSAGE,
  });
}

/**
 * « Optimiser mon CV » par modifications : UN appel, le CV tel qu'écrit et une liste de
 * modifications que la personne accepte ou refuse une à une. Consomme 1 cv_improve, et seulement si
 * au moins une modification est proposée. `uiLanguage` : langue des explications (le CV garde la sienne).
 */
export function improveCvChanges(input: CvImproveInput, offer?: CvOfferInput): Promise<CvChanges> {
  return invoke<CvChanges>(
    'claude-cv',
    { mode: 'improve_changes', ...input, ...(offer ? { offer } : {}), uiLanguage: currentLanguage() },
    'cv_improve',
    { unreadable_document: UNREADABLE_MESSAGE, ai_bad_output: UNREADABLE_MESSAGE },
  );
}

export interface CvRecommendations {
  recommendations: string[];
}

// Recommandations APRÈS génération, vis-à-vis d'une offre. Aucun quota (complément de la création).
export function recommendCvForOffer(cv: string, offer: CvOfferInput): Promise<CvRecommendations> {
  return invoke<CvRecommendations>('claude-cv', { mode: 'recommend', cv, offer }, 'cv_create', {
    unreadable_document: UNREADABLE_MESSAGE,
  });
}

/**
 * Version condensée d'un CV qui déborde d'une page à l'export. Aucun quota décompté (éligibilité
 * « export » seulement) ; le serveur ne fait appel à Claude que si le CV dépasse son budget.
 */
export function condenserCv(cv: GeneratedCv, outputLanguage?: OutputLanguage): Promise<GeneratedCv> {
  return invoke<GeneratedCv>('claude-cv', { mode: 'condense', cv, ...(outputLanguage ? { outputLanguage } : {}) }, 'export');
}

export function rewriteSection(section: string, content: string): Promise<RewrittenSection> {
  return invoke<RewrittenSection>(
    'claude-cv',
    { mode: 'rewrite', section, content },
    'section_rewrite',
  );
}

// ───────────── Simulateur d'entretien (multi-tours) ─────────────
// start consomme 1 quota interview_sim ; reply/feedback ne reconsomment pas (cf. handler).

export function startInterview(
  cv: string,
  context: string,
  role: InterviewRole = 'recruiter',
  offer?: CvOfferInput,
  language: OutputLanguage = 'fr',
): Promise<{ message: string; context?: string; voix?: string }> {
  return invoke<{ message: string; context?: string; voix?: string }>(
    'claude-interview',
    { mode: 'start', cv, context, role, language, ...(offer ? { offer } : {}) },
    'interview_sim',
    { unreadable_document: UNREADABLE_OFFER_MESSAGE },
  );
}

export function replyInterview(
  cv: string,
  context: string,
  history: InterviewMessage[],
  role: InterviewRole = 'recruiter',
  language: OutputLanguage = 'fr',
  conclure = false,
): Promise<{ message: string; voix?: string }> {
  return invoke<{ message: string; voix?: string }>(
    'claude-interview',
    { mode: 'reply', cv, context, role, language, history, conclure },
    'interview_sim',
  );
}

export function interviewFeedback(
  cv: string,
  context: string,
  history: InterviewMessage[],
  role: InterviewRole = 'recruiter',
  language: OutputLanguage = 'fr',
): Promise<InterviewFeedback> {
  return invoke<InterviewFeedback>(
    'claude-interview',
    { mode: 'feedback', cv, context, role, language, history },
    'interview_sim',
  );
}

/**
 * Génération d'une lettre de motivation (quota `cover_letter`).
 *
 * La date du jour est fournie par l'application, jamais devinée par le modèle (section 2 du
 * document). Le CV part sous sa forme structurée, telle qu'elle est en base.
 */
export async function generateCoverLetter(input: {
  cv: string;
  /**
   * Annonce : texte collé OU document importé. Même forme que `CvOfferInput` de la notation de CV,
   * plutôt qu'un type de plus — le serveur y applique le même traitement, extraction du .docx et
   * bloc document natif pour le PDF.
   */
  annonce: CvOfferInput;
  params: CoverLetterParams;
}): Promise<CoverLetter> {
  // Dans la typographie de la LANGUE de la lettre : voir utils/letterDate.
  const dateDuJour = letterDateDuJour(input.params.langue);

  return invoke<CoverLetter>('claude-letter', { ...input, dateDuJour }, 'cover_letter', {
    invalid_request: 'Vérifie les informations saisies, puis réessaie.',
    unreadable_document: UNREADABLE_OFFER_MESSAGE,
    // Le message générique parle d'« analyse » : faux ici, on rédige.
    ai_bad_output: t('letterCreate.erreurFormat'),
  });
}

/**
 * Le diagnostic porte aussi le TEXTE de la lettre analysée.
 *
 * `lettre_texte` existe pour une seule raison : avec un document importé, le client n'a pas le
 * texte, et `saveCoverLetter` en a besoin pour que la lettre rejoigne « Mes lettres ». C'est le
 * même motif que le mode `extract` de claude-cv, qui renvoie le CV structuré que l'écran persiste :
 * le serveur produit l'artefact, le client ne réextrait jamais.
 *
 * `texte_reconstitue` distingue la source : faux quand le texte est exact (collage, ou .docx extrait
 * côté serveur), vrai quand il a été TRANSCRIT par le modèle depuis un PDF. L'écran le dit à
 * l'utilisateur — c'est le seul défaut de ce choix, autant l'assumer que le laisser surprendre.
 */
/**
 * Analyse d'une lettre existante — mode `analyse` de claude-letter.
 *
 * Le score et le verdict sont RECONSTRUITS par le serveur à partir des sous-notes : le modèle ne
 * les annonce pas et ne connaît pas les pondérations. Rien à recalculer ni à vérifier ici.
 *
 * `coverLetterId` est facultatif : sans lui, le diagnostic est renvoyé mais pas conservé, faute de
 * lettre à laquelle le rattacher.
 */
export async function analyseCoverLetter(input: {
  /** Lettre à analyser : texte collé OU document importé (PDF, Word). */
  lettre: CvOfferInput;
  annonce: CvOfferInput;
  cv: string;
  profil: string;
  langue: string;
  metier: string;
  organisation: string;
  typeContrat: string;
  coverLetterId?: string;
}): Promise<LetterAnalysis> {
  return invoke<LetterAnalysis>('claude-letter', { ...input, mode: 'analyse' }, 'cover_letter_analyse', {
    invalid_request: 'Vérifie la lettre saisie, puis réessaie.',
    unreadable_document: UNREADABLE_MESSAGE,
  });
}

export interface LetterImproveInput {
  /** Objet de la lettre ; vide pour une lettre sans objet réel (collée ou importée). */
  objet: string;
  paragraphes: string[];
  /** Annonce en texte, facultative. */
  annonce: string;
  /** CV principal sérialisé, vide si la personne n'en a pas. */
  cv: string;
  profil: string;
  /** Langue de la lettre : celle des textes proposés. */
  langue: string;
  diagnostic?: LetterImproveDiagnostic;
}

/**
 * « Améliorer ma lettre » (1.2.0) : des modifications précises, paragraphe par paragraphe, que la
 * personne garde ou écarte. Abonnés seulement ; consomme 1 cover_letter_improve, et seulement si au
 * moins une modification est proposée. `uiLanguage` : langue des explications (la lettre garde la sienne).
 */
export function improveCoverLetter(input: LetterImproveInput): Promise<LetterChanges> {
  const { annonce, ...reste } = input;
  return invoke<LetterChanges>(
    'claude-letter',
    {
      ...reste,
      ...(annonce.trim() ? { annonce: { text: annonce.trim() } } : {}),
      mode: 'amelioration',
      uiLanguage: currentLanguage(),
    },
    'cover_letter_improve',
    { invalid_request: 'Vérifie ta lettre, puis réessaie.' },
  );
}
